import { createConsumer, type Consumer, type Subscription } from "@rails/actioncable";
import Cookies from "js-cookie";

import "../types/rails-actioncable.d.ts";

import humps from "humps";
import { jwtDecode } from "jwt-decode";
import { getCurrentSelectedTenant, getIsSjAdminFromStorage } from "../utils";
import { retrieveAuthToken } from "../utils/authService";
import { Attempt, LessonAttempt } from "../api/types/tournamentTypes";

const isLocal = import.meta.env.VITE_APP_ENVIRONMENT === "local";
const useSecureCookie =
  !isLocal && import.meta.env.VITE_APP_ENVIRONMENT !== "test" && !import.meta.env.CI;

// Give up refreshing auth after this many consecutive rejected connections so a
// broken backend cannot keep the client in a reconnect loop.
const MAX_RECONNECT_ATTEMPTS = 5;

let cable: Consumer | undefined;
let cablePromise: Promise<Consumer> | undefined;
let reconnectPromise: Promise<void> | undefined;
let reconnectAttempts = 0;

interface JwtPayload {
  available_tenants?: Array<{ name: string }>;
}

interface CableConnectionEvents {
  open?: (this: CableConnection, event: Event) => void;
  message?: (this: CableConnection, event: MessageEvent) => void;
  close?: (this: CableConnection, event: CloseEvent) => void;
  error?: (this: CableConnection, event: Event) => void;
}

interface CableConnection {
  events: CableConnectionEvents;
  monitor: { stop: () => void };
}

type CableWithConnection = Consumer & { connection: CableConnection };

// Channel message type definitions
export interface CodeSubmissionChannelMessage {
  status: string;
  attempt: LessonAttempt;
  email: string;
}

export interface CtfSubmissionChannelMessage {
  status: string;
  attempt: LessonAttempt;
  email: string;
}

export interface LessonCompletedChannelMessage {
  status: string;
  entity: Entity;
  email: string;
}

export interface TournamentExperimentMessage extends Attempt {
  attemptStatus: string;
  email: string;
  points: number;
}

export interface PathCompletedChannelMessage {
  status: string;
  roleEnrollmentId: number;
}

interface Entity {
  id: number;
  right_answers_count: number | null;
  result: string;
  display_score: string;
  assessment_score: number;
  show_after_survey: boolean;
  show_before_survey: boolean;
  answers_count: number;
  questions_count: number;
}

const findTenant = (authToken: string): string | null => {
  const currentSelectedTenant = getCurrentSelectedTenant();
  if (currentSelectedTenant) {
    return currentSelectedTenant;
  }
  const decoded = jwtDecode<JwtPayload>(authToken);
  const tenants = decoded.available_tenants;
  if (tenants?.length) {
    return tenants[0]?.name ?? null;
  }
  return null;
};

const setAuthTokenCookie = (token: string): void => {
  Cookies.set("authToken", token, {
    sameSite: "strict",
    expires: new Date(Date.now() + 60 * 1000),
    secure: useSecureCookie,
  });
};

const ensureCable = (): Promise<Consumer> => {
  if (cable) {
    return Promise.resolve(cable);
  }

  if (!cablePromise) {
    cablePromise = retrieveAuthToken()
      .then(authToken => {
        const baseParams = [
          "impersonate_authorization=",
          `current_selected_tenant=${findTenant(authToken)}`,
          `is_sj_admin=${!!getIsSjAdminFromStorage()}`,
        ];

        setAuthTokenCookie(authToken);
        cable = createConsumer(`/api/cable?${baseParams.join("&")}`);
        handleCableDisconnect((cable as CableWithConnection).connection);
        return cable;
      })
      .catch(error => {
        cablePromise = undefined;
        throw error;
      });
  }

  return cablePromise;
};

const reconnectWithFreshAuthToken = (): void => {
  if (reconnectPromise) {
    return;
  }

  reconnectPromise = retrieveAuthToken({ forceRefresh: true })
    .then(setAuthTokenCookie)
    .then(() => cable?.connect())
    .catch(error => {
      console.error("Unable to refresh ActionCable authentication", error);
    })
    .finally(() => {
      reconnectPromise = undefined;
    });
};

// The socket opening is not proof of a working connection: the server accepts the
// WebSocket and only then rejects bad auth. A `welcome` frame means we are really in.
const isWelcomeMessage = (event: MessageEvent): boolean => {
  try {
    return (JSON.parse(event.data as string) as { type?: string }).type === "welcome";
  } catch {
    return false;
  }
};

const handleCableDisconnect = (connection: CableConnection): void => {
  const originalClose = connection.events.close;
  if (!originalClose) {
    return;
  }

  const originalMessage = connection.events.message;

  connection.events = {
    ...connection.events,
    message: function (event) {
      if (isWelcomeMessage(event)) {
        reconnectAttempts = 0;
      }
      originalMessage?.call(this, event);
    },
    close: event => {
      // Prevent ActionCable from retrying with the rejected token while it is refreshed.
      connection.monitor.stop();
      originalClose.call(connection, event);

      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.error(
          `ActionCable connection closed after ${MAX_RECONNECT_ATTEMPTS} authentication refresh attempts; giving up.`
        );
        return;
      }

      reconnectAttempts += 1;
      console.log(
        `ActionCable connection closed, attempting to refresh auth token (attempt ${reconnectAttempts} of ${MAX_RECONNECT_ATTEMPTS})`
      );
      reconnectWithFreshAuthToken();
    },
  };
};

interface InitCableOptions {
  onDisconnect?: () => void;
  onConnect?: () => void;
  camelizeKeys?: boolean;
}

export const initCable = <T = unknown>(
  channelName: string,
  onReceive: (data: T) => void,
  { onDisconnect, onConnect, camelizeKeys = false }: InitCableOptions = {}
): Promise<Subscription> => {
  return ensureCable().then(activeCable =>
    activeCable.subscriptions.create(
      { channel: channelName },
      {
        connected: () => {
          console.log(`${channelName} connected`);
          if (onConnect) {
            onConnect();
          }
        },

        disconnected: () => {
          console.log(`${channelName} disconnected`);
          if (onDisconnect) {
            onDisconnect();
          }
        },

        received: (data: object) => {
          if (camelizeKeys) {
            data = humps.camelizeKeys(data);
          }
          onReceive(data as T);
        },
      }
    )
  );
};

export const NOTIFICATIONS_CHANNEL = "NotificationsChannel";
export const DOJO_ACTIVITY_CHANNEL = "DojoActivityChannel";
export const CUSTOM_NOTIFICATIONS_CHANNEL = "CustomNotificationsChannel";
export const MAYHEM_CHANNEL = "MayhemChannel";
export const IMPORT_USERS_CHANNEL = "ImportUsersChannel";
export const IMPORT_FROM_HEROKU_CHANNEL = "ImportFromHerokuChannel";
export const IMPORT_FROM_HACKEDU_CHANNEL = "ImportFromHackeduChannel";
export const UPLOAD_ASSIGNMENT_USERS_CHANNEL = "UploadAssignmentUsersChannel";
export const CREATE_VIRTUAL_DOJO_CHANNEL = "CreateVirtualDojoChannel";
export const CREATE_ASSIGNMENTS_CHANNEL = "CreateAssignmentsChannel";
export const UNLOCK_ALL_USERS_CHANNEL = "UnlockAllUsersChannel";
export const SYNCHRONIZE_CONTENT_CHANNEL = "SynchronizeContentChannel";
export const CREATE_LEVEL_WITH_PREFERENCES_CHANNEL = "CreateLevelWithPreferencesChannel";
export const DOWNLOAD_NOTES_CHANNEL = "DownloadNotesChannel";
export const VIRTUAL_DOJO_CURRENT_STATUS_CHANNEL = "VirtualDojoCurrentStatusChannel";
export const LESSON_COMPLETED_CHANNEL = "LessonCompletedChannel";
export const CODE_SUBMISSION_CHANNEL = "CodeSubmissionChannel";
export const CTF_SUBMISSION_CHANNEL = "CtfSubmissionChannel";
export const EXPERIMENT_CODE_SUBMISSION_CHANNEL = "ExperimentCodeSubmissionChannel";
export const TOURNAMENT_EXPERIMENT_CODE_SUBMISSION_CHANNEL =
  "TournamentExperimentCodeSubmissionChannel";
export const ARCHIVE_USER_CHANNEL = "ArchiveUserChannel";
export const PATH_COMPLETED_CHANNEL = "PathCompletedChannel";

export const initLessonCompletedChannel = (
  onReceive: (data: LessonCompletedChannelMessage) => void
): Promise<Subscription> => {
  return initCable(LESSON_COMPLETED_CHANNEL, onReceive, { camelizeKeys: true });
};

export const initCodeSubmissionChannel = (
  onReceive: (data: CodeSubmissionChannelMessage) => void
): Promise<Subscription> => {
  return initCable(CODE_SUBMISSION_CHANNEL, onReceive, { camelizeKeys: true });
};

export const initCtfSubmissionChannel = (
  onReceive: (data: CtfSubmissionChannelMessage) => void
): Promise<Subscription> => {
  return initCable(CTF_SUBMISSION_CHANNEL, onReceive, { camelizeKeys: true });
};

export const initTournamentExperimentCodeSubmissionChannel = (
  onReceive: (data: TournamentExperimentMessage) => void
): Promise<Subscription> => {
  return initCable(TOURNAMENT_EXPERIMENT_CODE_SUBMISSION_CHANNEL, onReceive, {
    camelizeKeys: true,
  });
};

export const initNotificationsChannel = (
  onReceive: (data: { status: boolean }) => void
): Promise<Subscription> => {
  return initCable(NOTIFICATIONS_CHANNEL, onReceive, { camelizeKeys: true });
};

export const initPathCompletedChannel = (
  onReceive: (data: PathCompletedChannelMessage) => void
): Promise<Subscription> => {
  return initCable(PATH_COMPLETED_CHANNEL, onReceive, { camelizeKeys: true });
};
